import React from 'react';
import PropTypes from 'prop-types';
import styles from './Checkbox.css';
import classnames from 'classnames';
import testable from '@xingternal/malt-util/testable';
import { QA_MALT } from '@xingternal/malt-constants';
import { IconShapeTick } from '@xingternal/malt-icon';

const TestableLabel = testable(QA_MALT, 'label');

const INPUT_TYPE = 'checkbox';

/**
 This is a [controlled form component](https://facebook.github.io/react/docs/forms.html#controlled-components).
 It has no internal state. Instead whatever you pass as the `checked` property will be displayed. You need to
 provide it with an `onChange` handler that needs to update state with the details being under your control.

 Example:

 ```javascript
const checked = true;
<Checkbox checked={checked} onChange={event => doSomething(event.target.checked)}>
  Label for Box
</Checkbox>
 ```
 */

const Checkbox = ({
  disabled,
  children,
  onChange,
  checked,
  className,
  name,
  testId,
}) => {
  return (
    <TestableLabel
      className={classnames(styles.checkboxLabel, className)}
      onClick={event => {
        if (event.target.type !== INPUT_TYPE) {
          event.stopPropagation();
        }
      }}
      testId={testId}
    >
      <input
        type={INPUT_TYPE}
        className={styles.input}
        disabled={disabled}
        onChange={onChange}
        checked={checked}
        name={name}
      />
      <div className={styles.box}>
        <IconShapeTick className={styles.tick} />
      </div>
      <span className={styles.text}>{children}</span>
    </TestableLabel>
  );
};

Checkbox.propTypes = {
  /** Renders the checkbox text */
  children: PropTypes.node.isRequired,
  /** Pass a custom class to the surrounding label of the checkbox */
  className: PropTypes.string,
  /** Determines if the checkbox is checked or not. */
  checked: PropTypes.bool,
  /** Callback function passing complete unprocessed event object. */
  onChange: PropTypes.func,
  /** disables checkbox */
  disabled: PropTypes.bool,
  /** set the input name */
  name: PropTypes.string,
  /** define a testid for the checkbox */
  testId: PropTypes.string,
};

Checkbox.displayName = 'Checkbox';

export default Checkbox;
